// Pure helpers for the top-up instalment-plan model (tv-w98, Feature B
// foundation). No store reads and no `Date.now()` — the reconciler (tv-opw),
// chase logic (tv-qta) and builder UI (tv-1a9) reuse these against the sim clock.

import { differenceInCalendarDays } from 'date-fns';
import { MIN_TOPUP_INSTALMENT_AUD as DOMAIN_MIN, spreadInstalments } from '@atink/domain';
import type { TopupFrequency, TopupInstalment, TopupPlan } from '@/domain/types';

/** The $50 floor every instalment keeps — the server's rule too (@atink/domain, B4 75my). */
export const MIN_TOPUP_INSTALMENT_AUD = DOMAIN_MIN;

/** Whole AUD dollars paid so far (sum of `paid` instalments). */
export function topupPaidAud(plan: TopupPlan): number {
  return plan.instalments.reduce(
    (sum, i) => (i.status === 'paid' ? sum + i.amountAud : sum),
    0,
  );
}

/** Whole AUD dollars still owed on the plan (never below zero). */
export function topupOutstandingAud(plan: TopupPlan): number {
  return Math.max(0, plan.totalAud - topupPaidAud(plan));
}

/**
 * The earliest still-`scheduled` instalment whose due date has arrived
 * (`dueIso <= nowIso`) — the one now due for a reminder. `null` when nothing is
 * due yet. Paid/reminded/missed/cancelled instalments are ignored.
 */
export function nextDueInstalment(plan: TopupPlan, nowIso: string): TopupInstalment | null {
  const now = new Date(nowIso).getTime();
  let due: TopupInstalment | null = null;
  for (const i of plan.instalments) {
    if (i.status !== 'scheduled') continue;
    if (new Date(i.dueIso).getTime() > now) continue;
    if (due === null || new Date(i.dueIso).getTime() < new Date(due.dueIso).getTime()) {
      due = i;
    }
  }
  return due;
}

/** True once every instalment is settled (paid or cancelled) and nothing is outstanding. */
export function isTopupPlanComplete(plan: TopupPlan): boolean {
  const allSettled = plan.instalments.every(
    (i) => i.status === 'paid' || i.status === 'cancelled',
  );
  return allSettled && topupOutstandingAud(plan) <= 0;
}

const INTERVAL_DAYS: Record<TopupFrequency, number> = { weekly: 7, fortnightly: 14 };

/**
 * Build the `scheduled` instalment schedule for a plan from a start date, a
 * cadence and a per-instalment amount — the ONE rule the server enforces
 * (@atink/domain spreadInstalments, B4 75my): `instalmentAud` each, the
 * remainder as the last instalment, folded into the one before when it would be
 * under $50. It sums to exactly `totalAud`.
 */
export function generateInstalments(opts: {
  startIso: string;
  frequency: TopupFrequency;
  instalmentAud: number;
  totalAud: number;
}): TopupInstalment[] {
  const { startIso, frequency, instalmentAud, totalAud } = opts;
  if (totalAud <= 0) return [];
  return spreadInstalments({ totalAud, instalmentAud, frequency, startIso }).map((i) => ({
    dueIso: i.dueIso,
    amountAud: i.amountAud,
    status: 'scheduled' as const,
  }));
}

/**
 * How many instalment due-dates fall on or before the appointment, starting at
 * `firstIso` and stepping by the cadence. Min 1 (the first instalment always
 * counts, even if it is already at/after the appointment).
 */
function periodsUntilAppointment(firstIso: string, appointmentIso: string, frequency: TopupFrequency): number {
  const days = differenceInCalendarDays(new Date(appointmentIso), new Date(firstIso));
  if (days <= 0) return 1;
  return Math.floor(days / INTERVAL_DAYS[frequency]) + 1;
}

/**
 * Per-instalment amount that clears `totalAud` within the periods available
 * before the appointment, floored at the $50 minimum. When the whole deposit is
 * at/under the floor, the deposit itself is the single instalment.
 */
export function suggestInstalmentAud(opts: {
  totalAud: number;
  firstIso: string;
  appointmentIso: string;
  frequency: TopupFrequency;
}): number {
  const { totalAud, firstIso, appointmentIso, frequency } = opts;
  if (totalAud <= MIN_TOPUP_INSTALMENT_AUD) return totalAud;
  const periods = periodsUntilAppointment(firstIso, appointmentIso, frequency);
  return Math.max(MIN_TOPUP_INSTALMENT_AUD, Math.ceil(totalAud / periods));
}

/** True when the last scheduled instalment falls after the appointment date. */
export function scheduleOverrunsAppointment(instalments: TopupInstalment[], appointmentIso: string): boolean {
  if (instalments.length === 0) return false;
  const last = instalments[instalments.length - 1]!;
  return differenceInCalendarDays(new Date(last.dueIso), new Date(appointmentIso)) > 0;
}
